import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardPage from "../../layout/index";
import Button from "../../components/Button";
import Loader from "../../components/Loader";
import { getCustomerDetails, generateCheckInOtp } from "../../api/supervisor";
import { toast, ToastContainer } from "react-toastify";

interface CustomerDetails {
  id?: string;
  outlet_id?: string;
  outlet_name_en?: string;
  outlet_name_ar?: string;
  mobile?: string;
  email?: string;
  [key: string]: any;
}

const OtpGeneration: React.FC = () => {
  const { t } = useTranslation();
  const [customerId, setCustomerId] = useState("");
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails | null>(null);
  const [generatedOtp, setGeneratedOtp] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [customerNotFound, setCustomerNotFound] = useState(false);
  const [errorMessage, setErrorMessage] = useState(""); // <-- Added for error display

  const validateCustomerId = (id: string): boolean => {
    return id.length <= 10;
  };

  const handleCustomerIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomerId(value);

    // Clear previous data when customer ID changes
    if (customerDetails || generatedOtp || errorMessage) {
      setCustomerDetails(null);
      setGeneratedOtp("");
      setErrorMessage("");
    }
  };

  const handleCustomerIdBlur = async () => {
    if (!validateCustomerId(customerId)) {
      return;
    }

    setLoading(true);
    setCustomerNotFound(false); // Reset not found state
    try {
      const response = await getCustomerDetails(customerId);
      if (!response.data || Object.keys(response.data).length === 0) {
        setCustomerDetails(null);
        setCustomerNotFound(true);
      } else {
        setCustomerDetails(response.data);
        setCustomerNotFound(false);
      }
    } catch (error: any) {
      console.error("Error fetching customer details:", error);
      setCustomerDetails(null);
      setCustomerNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isCustomerIdValid && !loading) {
      handleCustomerIdBlur();
    }
  };

  const handleGenerateOtp = async () => {
    if (!customerDetails) {
      return;
    }

    setOtpLoading(true);
    setErrorMessage(""); // Clear previous error
    try {
      const response = await generateCheckInOtp(customerId);
      const otp = response.data?.otp || response.data?.data?.otp;
      if (otp) {
        setGeneratedOtp(otp);
        setErrorMessage(""); // Clear error if OTP is generated
      }
    } catch (error: any) {
      console.error("Error generating OTP:", error);
      const detail = error?.response?.data?.detail;
      let msg = "";
      if (detail?.error_code === "max_checkin_otp_limit_exceeded") {
        msg = detail?.message || "Maximum check-in OTP limit has been reached for this customer";
      } else if (typeof detail === "string") {
        msg = detail;
      } else {
        msg = "Failed to generate OTP. Please try again.";
      }
      setErrorMessage(msg); // Set error message here
      setGeneratedOtp(""); // Clear OTP if error occurs
    } finally {
      setOtpLoading(false);
    }
  };

  const handleClear = () => {
    setCustomerId("");
    setCustomerDetails(null);
    setGeneratedOtp("");
    setCustomerNotFound(false);
    setErrorMessage(""); // Clear error on clear
  };

  const isCustomerIdValid = validateCustomerId(customerId);
  const canGenerateOtp = customerDetails && !otpLoading;

  return (
    <DashboardPage>
      <ToastContainer />
      <div className="bg-gray-50 sm:pl-0 pr-4">
        <div className="px-6 flex flex-col gap-2">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-0">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              OTP Generation
            </h1>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            {/* Customer ID Input */}
            <div className="mb-6">
              <label htmlFor="customerId" className="block text-sm font-medium text-gray-700 mb-2">
                Customer ID *
              </label>
              <div className="flex gap-3">
                <div className="relative w-80">
                  <input
                    id="customerId"
                    type="text"
                    value={customerId}
                    onChange={handleCustomerIdChange}
                    onBlur={handleCustomerIdBlur}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter Customer ID"
                    autoComplete="off"
                    className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan ${
                      customerId && !isCustomerIdValid
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:ring-cyan"
                    }`}
                    maxLength={10}
                  />
                  <button
                    onClick={handleCustomerIdBlur}
                    disabled={!isCustomerIdValid || loading}
                    className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded-md transition-colors ${
                      isCustomerIdValid && !loading
                        ? "text-cyan hover:bg-cyan hover:text-white"
                        : "text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </button>
                </div>
                {loading && (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan"></div>
                    <span className="ml-2 text-sm text-gray-600">Loading...</span>
                  </div>
                )}
              </div>
              {customerId && !isCustomerIdValid && (
                <p className="text-red-500 text-sm mt-1">
                  Invalid Customer ID. Please enter a 10-digit customer ID.
                </p>
              )}
              {isCustomerIdValid && !loading && (
                <p className="text-gray-500 text-sm mt-1">
                  Click the search icon or press Enter to fetch customer details
                </p>
              )}
              {customerNotFound && !loading && (
                <p className="text-red-500 text-sm mt-2">
                  Customer not found. Please check the Customer ID and try again.
                </p>
              )}
            </div>

            {/* Customer Details */}
            {loading && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Customer Details
                </h3>
                <div className="flex items-center justify-center py-8">
                  <Loader />
                </div>
              </div>
            )}

            {customerDetails && !loading && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Customer Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Outlet ID</p>
                    <p className="font-medium">{customerDetails.outlet_id || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Name (English)</p>
                    <p className="font-medium">{customerDetails.outlet_name_en || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Name (Arabic)</p>
                    <p className="font-medium">{customerDetails.outlet_name_ar || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Mobile Number</p>
                    <p className="font-medium">{customerDetails.mobile || "N/A"}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Generated OTP or Error Display */}
            {(generatedOtp || errorMessage) && (
              <div className={`mb-6 p-6 ${errorMessage ? "bg-red-50 border border-red-200" : "bg-green-50 border border-green-200"} rounded-lg`}>
                <h3 className={`text-base font-semibold mb-3 ${errorMessage ? "text-red-900" : "text-green-900"}`}>
                  {errorMessage ? "Error" : "Generated OTP"}
                </h3>
                <div className="text-center">
                  {errorMessage ? (
                    <p className="text-xl font-bold text-red-600 tracking-wider">
                      {errorMessage}
                    </p>
                  ) : (
                    <>
                      <p className="text-4xl font-bold text-green-600 tracking-wider">
                        {generatedOtp}
                      </p>
                      <p className="text-sm text-green-700 mt-2">
                        This OTP is only valid for 10 minutes.
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Generate OTP Button */}
            {customerDetails && (
              <div className="mb-6">
                <Button
                  label={otpLoading ? "Generating OTP..." : "Generate OTP"}
                  type="primary"
                  disabled={!canGenerateOtp}
                  onClick={handleGenerateOtp}
                />
              </div>
            )}

            {/* Clear Button */}
            {(customerDetails || generatedOtp || errorMessage) && (
              <div className="flex gap-3">
                <Button
                  label="Clear All"
                  type="secondary"
                  onClick={handleClear}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardPage>
  );
};

export default OtpGeneration;